import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SquareLoader } from "react-spinners";
import axiosWithAuth from "../../../utils/axiosWithAuth";
import { getApiEndpoint } from "../../../utils/apiConfig";
import Header from "../../../components/Header";
import "./MyHome.scss"; // Ana stil dosyasını import ediyoruz
import { getAllBreakdowns } from "../../../services/breakdownService";
import { getUserProperties } from "../../../services/propertyService";

const MyHome = () => {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [breakdowns, setBreakdowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useBreakdownsAsProperties, setUseBreakdownsAsProperties] = useState(false);
  document.title = t("myHome.0");
  const headerTitle = t("myHome.0");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Get user info from localStorage
        const storedUser = localStorage.getItem("user");

        if (!storedUser) {
          console.warn("User information not found");
          setError(t("account.1"));
          setLoading(false);
          return;
        }

        const parsedUser = JSON.parse(storedUser);
        const userId = parsedUser.id || parsedUser._id;
        
        console.log("User ID:", userId);
        
        if (!userId) {
          console.warn("Valid user ID not found");
          setError(t("login.1"));
          setLoading(false);
          return;
        }

        try {
          // Use repairs/calculations endpoint to get RepairCalculation model data
          console.log("Fetching calculations from API...");
          const api = axiosWithAuth();
          const breakdownResponse = await api.get(getApiEndpoint('repairs/calculations'));
          
          console.log("API Response:", breakdownResponse.data);
          
          // Check for success:true response structure
          if (breakdownResponse && 
              breakdownResponse.data && 
              breakdownResponse.data.success && 
              Array.isArray(breakdownResponse.data.data)) {
            setBreakdowns(breakdownResponse.data.data);
            console.log("Calculations fetched:", breakdownResponse.data.data);
          }
          // Fallback for direct array response
          else if (breakdownResponse && 
              breakdownResponse.data && 
              Array.isArray(breakdownResponse.data)) {
            setBreakdowns(breakdownResponse.data);
            console.log("Calculations fetched (direct array):", breakdownResponse.data);
          }
          
          // Şimdi properties'leri alalım
          console.log("Fetching properties from API...");
          const propertiesResponse = await getUserProperties();
          
          // Validate properties data
          if (propertiesResponse && 
              propertiesResponse.data && 
              Array.isArray(propertiesResponse.data) && 
              propertiesResponse.data.length > 0) {
                
            // Process properties to ensure they have valid IDs
            const validProperties = propertiesResponse.data.filter(property => 
              property && (property._id || property.id)
            ).map(property => ({
              ...property,
              // Ensure property always has an id field
              id: property.id || property._id,
              // Ensure property type and status have proper format
              propertyType: formatPropertyType(property.propertyType),
              propertyStatus: formatPropertyStatus(property.propertyStatus)
            }));
            
            if (validProperties.length > 0) {
              console.log("Properties fetched successfully:", validProperties);
              setProperties(validProperties);
              setUseBreakdownsAsProperties(false);
              setLoading(false);
              return;
            }
          }
          
          // Eğer properties yoksa, breakdown'ları kullanmayı dene
          const calculationsData = breakdownResponse && 
              breakdownResponse.data && 
              breakdownResponse.data.success ? 
              breakdownResponse.data.data : 
              (Array.isArray(breakdownResponse.data) ? breakdownResponse.data : []);
              
          if (calculationsData && calculationsData.length > 0) {
            
            console.log("No properties found, using calculations to create virtual properties");
            console.log("Calculations data:", calculationsData);
            
            // Benzersiz calculation'ları grupla (birden fazla calculation aynı style/tariff kombinasyonu için olabilir)
            const uniqueCalculationGroups = {};
            
            calculationsData.forEach(calculation => {
              // Style ve tariff birleşimini benzersiz key olarak kullan
              const styleId = calculation.repairStyle?._id || calculation.repairStyle?.id || 'no-style';
              const tariffId = calculation.repairTariff?._id || calculation.repairTariff?.id || 'no-tariff';
              const calculationId = calculation._id; // Her calculation için benzersiz ID
              
              // Her calculation için ayrı bir key oluştur
              const key = `${styleId}-${tariffId}-${calculationId}`;
              
              // Her calculation için ayrı bir grup oluştur
              uniqueCalculationGroups[key] = {
                style: calculation.repairStyle,
                tariff: calculation.repairTariff,
                calculation: calculation,
                calculationCount: 1
              };
            });
            
            // Gruplardan sanal property'ler oluştur
            const virtualProperties = Object.values(uniqueCalculationGroups).map((group, index) => {
              const calculation = group.calculation;
              
              // Style ve tariff adlarını düzgün şekilde al
              let styleName = t("calculator.title"); // Default value
              let tariffName = t("calculator.type"); // Default value
              
              if (group.style && group.style.name) {
                styleName = group.style.name.text || group.style.name.az || group.style.name.en || styleName;
              }
              
              if (group.tariff && group.tariff.name) {
                tariffName = group.tariff.name.text || group.tariff.name.az || group.tariff.name.en || tariffName;
              }
              
              return {
                id: `calculation-${calculation._id}`,
                _id: calculation._id,
                breakdownId: calculation._id,
                name: `${styleName} (${tariffName})`,
                isVirtual: true,
                roomCount: calculation.breakdown?.rooms?.length || 0,
                doorCount: calculation.breakdown?.fixtures?.doors?.count || 0,
                windowCount: calculation.breakdown?.fixtures?.windows?.count || 0,
                area: calculateTotalArea(calculation),
                propertyType: {
                  name: { text: styleName },
                  iconType: "fa-solid fa-file-invoice-dollar"
                },
                propertyStatus: {
                  name: { text: tariffName }
                },
                breakdownCount: 1, // Her calculation ayrı olduğu için 1
                costs: calculation.costs,
                generalCost: calculation.costs?.generalCost || 0,
                currency: calculation.costs?.currency || "AZN",
                createdAt: calculation.createdAt
              };
            });
            
            setProperties(virtualProperties);
            setUseBreakdownsAsProperties(true);
            setLoading(false);
            return;
          }
          
          // Hiçbir veri bulunamadıysa
          setProperties([]);
          setLoading(false);
        } catch (apiError) {
          console.error("API request failed:", apiError);
          setError(t("myHome.noPropertiesFound"));
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching property data:", error);
        setError(t("myHome.noPropertiesFound"));
        setLoading(false);
      }
    };

    fetchUserData();
  }, [t]);
  
  // Breakdown'dan toplam alanı hesapla
  const calculateTotalArea = (breakdown) => {
    if (!breakdown.breakdown || !breakdown.breakdown.rooms) return 0;
    
    return breakdown.breakdown.rooms.reduce((total, room) => {
      return total + (parseFloat(room.area) || 0);
    }, 0);
  };
  
  // Helper function to ensure property type has the expected format
  const formatPropertyType = (propertyType) => {
    if (!propertyType) {
      return {
        name: { text: t("homePlan.3") },
        iconType: "fa-solid fa-home"
      };
    }
    
    if (typeof propertyType === 'string') {
      return {
        id: propertyType,
        name: { text: t("homePlan.3") },
        iconType: "fa-solid fa-home"
      };
    }
    
    // Make sure name exists and has a text field
    if (!propertyType.name || !propertyType.name.text) {
      propertyType.name = { 
        ...(propertyType.name || {}),
        text: t("homePlan.3")
      };
    }
    
    // Make sure iconType exists
    if (!propertyType.iconType) {
      propertyType.iconType = "fa-solid fa-home";
    }
    
    return propertyType;
  };
  
  // Helper function to ensure property status has the expected format
  const formatPropertyStatus = (propertyStatus) => {
    if (!propertyStatus) return null;
    
    if (typeof propertyStatus === 'string') {
      return {
        id: propertyStatus,
        name: { text: "" }
      };
    }
    
    // Make sure name exists and has a text field
    if (!propertyStatus.name || !propertyStatus.name.text) {
      propertyStatus.name = { 
        ...(propertyStatus.name || {}),
        text: ""
      };
    }
    
    return propertyStatus;
  };
  
  // Categorize properties
  const categorizeProperties = () => {
    // Breakdown'lar mı property'ler olarak kullanılıyor?
    if (useBreakdownsAsProperties) {
      return {
        [t("general.calculations") || "Calculations"]: properties
      };
    }
    
    // Normal property kategorilendirme
    const categories = {
      // Use translation keys for categories
      [t("homePlan.housesCategory") || "Houses"]: [],
      [t("homePlan.apartmentsCategory") || "Apartments"]: [],
      [t("account.15") || "Other"]: []
    };
    
    properties.forEach(property => {
      const propertyTypeName = property.propertyType?.name?.text;
      
      if (!propertyTypeName) {
        categories[t("account.15") || "Other"].push(property);
        return;
      }
      
      // Categorize by property type
      if (propertyTypeName.includes("ev") || propertyTypeName.includes("house") || propertyTypeName.includes("villa")) {
        categories[t("homePlan.housesCategory") || "Houses"].push(property);
      } else if (propertyTypeName.includes("apart") || propertyTypeName.includes("flat") || propertyTypeName.includes("condo")) {
        categories[t("homePlan.apartmentsCategory") || "Apartments"].push(property);
      } else {
        categories[t("account.15") || "Other"].push(property);
      }
    });
    
    // Filter out empty categories
    const filteredCategories = {};
    Object.keys(categories).forEach(key => {
      if (categories[key].length > 0) {
        filteredCategories[key] = categories[key];
      }
    });
    
    return filteredCategories;
  };

  const handlePropertyClick = (property) => {
    if (property) {
      // Normal property için
      if (property.id && !property.isVirtual) {
        navigate(`/account/myhome/${property.id}`);
      } 
      // Breakdown-based sanal property için
      else if (property.breakdownId) {
        // Calculation data'yı AllMaterials sayfasına yönlendir
        if (breakdowns && breakdowns.length > 0) {
          // Breakdown'ı bul
          const breakdown = breakdowns.find(b => b._id === property.breakdownId);
          if (breakdown) {
            console.log("Navigating to AllMaterials with breakdown data");
            // AllMaterials sayfasına yönlendir
            navigate(`/allmaterials/${property.breakdownId}?breakdown=true`, {
              state: { breakdownData: breakdown }
            });
            return;
          }
        }
        // Eğer breakdown bulunamazsa normal yönlendirme yap
        navigate(`/allmaterials/${property.breakdownId}?breakdown=true`);
      }
    }
  };

  const createVirtualPropertyFromBreakdown = (breakdown, index) => {
    // Stil ve tarif adları için en->az geri dönüş mantığı
    const styleName = breakdown.style?.name?.text || breakdown.style?.name?.az || 'Unknown Style';
    const tariffName = breakdown.tariff?.name?.text || breakdown.tariff?.name?.az || 'Unknown Tariff';

    return {
      _id: `virtual-${breakdown._id}`,
      name: `${styleName} (${tariffName})`,
      isVirtual: true,
      area: breakdown.totalArea || 0,
      roomCount: breakdown.roomTotals?.roomCount || 0,
      doorCount: (breakdown.roomTotals?.doorCount || 0) + (breakdown.roomTotals?.exteriorDoorCount || 0),
      windowCount: breakdown.roomTotals?.windowCount || 0,
      breakdownId: breakdown._id,
      breakdownCount: 1,
      propertyId: breakdown.property?._id,
      createdAt: breakdown.createdAt,
      generalCost: breakdown.costs?.generalCost,
      currency: breakdown.costs?.currency
    };
  };

  if (loading) {
    return (
      <div className="loader-container">
        <SquareLoader
          color="#cda742"
          size={80}
          speedMultiplier={1}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-home-page">
        <Header title={headerTitle} showBackIcon={true} showProfileIcon={true} />
        <div className="alert alert-danger mt-3 mx-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      </div>
    );
  }

  if (!properties || properties.length === 0) {
    return (
      <div className="my-home-page">
        <Header title={headerTitle} showBackIcon={true} showProfileIcon={true} />
        <div className="alert alert-info mt-3 mx-3">
          <i className="bi bi-info-circle me-2"></i>
          {t("myHome.noPropertiesFound")}
        </div>
      </div>
    );
  }

  const categories = categorizeProperties();

  return (
    <div className="my-home-page">
      <Header
        title={headerTitle}
        showProfileIcon={true}
        showBackIcon={true}
        onBackClick={() => navigate("/account")}
      />

      {/* Add Orders Link */}
      <div className="orders-link-section mb-4 mx-3">
        <button 
          className="btn py-3 global-button w-100"
          onClick={() => navigate('/account/orders')}
        >
          {t("myHome.viewOrders")}
        </button>
      </div>
      
      <div className="property-categories">
        {Object.entries(categories).map(([categoryName, propertyList], index) => (
          <div key={index} className="property-category">
            <h4 className="category-title">{categoryName}</h4>
            
            <div className="property-cards">
              {propertyList.map((property, itemIndex) => (
                <div 
                  key={itemIndex} 
                  className={`calculation-card ${property.isVirtual ? 'virtual-calculation' : ''}`}
                  onClick={() => handlePropertyClick(property)}
                >
                  <div className="card-header">
                    <div className="calculation-badge">
                      <i className="fa-solid fa-calculator"></i>
                      <span>{t("general.calculation")}</span>
                    </div>
                    <div className="calculation-date">
                      {new Date(property.createdAt).toLocaleDateString('tr-TR')}
                    </div>
                  </div>
                  
                  <div className="card-content">
                    <div className="calculation-title">
                      <h3>{property.isVirtual ? property.name : (property.propertyType?.name?.text || t("homePlan.3"))}</h3>
                      <div className="calculation-id">#{property._id?.slice(-6) || 'N/A'}</div>
                    </div>
                    
                    <div className="calculation-stats">
                      <div className="stat-group">
                        <div className="stat-item primary">
                          <div className="stat-icon">
                            <i className="fa-solid fa-expand-arrows-alt"></i>
                          </div>
                          <div className="stat-info">
                            <span className="stat-value">{property.area}</span>
                            <span className="stat-label">m²</span>
                          </div>
                        </div>
                        
                        <div className="stat-item">
                          <div className="stat-icon">
                            <i className="fa-solid fa-door-open"></i>
                          </div>
                          <div className="stat-info">
                            <span className="stat-value">{property.roomCount}</span>
                            <span className="stat-label">{t("general.room")}</span>
                          </div>
                        </div>
                        
                        <div className="stat-item">
                          <div className="stat-icon">
                            <i className="fa-solid fa-door-closed"></i>
                          </div>
                          <div className="stat-info">
                            <span className="stat-value">{property.doorCount}</span>
                            <span className="stat-label">{t("general.door")}</span>
                          </div>
                        </div>
                        
                        <div className="stat-item">
                          <div className="stat-icon">
                            <i className="fa-regular fa-window-maximize"></i>
                          </div>
                          <div className="stat-info">
                            <span className="stat-value">{property.windowCount}</span>
                            <span className="stat-label">{t("general.window")}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-footer">
                    <div className="cost-display">
                      <div className="cost-amount">
                        <span className="currency-symbol">₼</span>
                        <span className="amount">{property.generalCost || property.costs?.generalCost || 0}</span>
                      </div>
                      <div className="cost-label">{t("general.totalCostLabel")}</div>
                    </div>
                    
                    <div className="action-button">
                      <i className="fa-solid fa-arrow-right"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyHome;
